import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import {
  RefreshCw,
  Search,
  Database,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";

const VoiceUsageSync = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState({ type: "", message: "" });

  const queryPath =
    startDate && endDate
      ? `/voice/voice-usage-clientwise?startDate=${startDate}&endDate=${endDate}`
      : `/voice/voice-usage-clientwise`;

  // Pass 'true' so it always fetches on component mount (defaulting to ALL data)
  const { data, isLoading, isError, error, refetch } = useApiEp(
    queryPath,
    true
  );

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncStatus({
      type: "info",
      message:
        !startDate && !endDate
          ? "Syncing ALL historical... Please wait."
          : "Syncing voice data for selected range...",
    });

    try {
      const response = await fetch(
        `${API_ENDPOINT}/voice/voice-usage-clientwise-sync`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify({
            startDate: startDate || null,
            endDate: endDate || null,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setSyncStatus({
          type: "success",
          message: "Data synced successfully! Updating table...",
        });
        if (refetch) refetch();
      } else {
        setSyncStatus({
          type: "error",
          message: result.message || "Failed to sync voice data.",
        });
      }
    } catch (err) {
      console.error(err);
      setSyncStatus({
        type: "error",
        message:
          "Network timeout or error. The sync is likely still running in the background. Check back in a few minutes.",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Helper to convert total billing duration seconds into hours and minutes
  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    let parts = [];
    if (hrs > 0) parts.push(`${hrs}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(" ");
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
      <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Database size={24} color="#1976d2" />
            <Typography variant="h5" component="h1" fontWeight="bold">
              Voice Usage Analytics
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            View aggregated billing durations and call statistics per client.
          </Typography>

          <Divider sx={{ my: 2.5 }} />

          {/* Filters & Actions Bar */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />

            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />

            <Button
              variant="outlined"
              color="primary"
              startIcon={<Search size={18} />}
              onClick={() => {
                if ((startDate && !endDate) || (!startDate && endDate)) {
                  alert(
                    "Please select both a start and end date, or clear both."
                  );
                  return;
                }
                if (refetch) refetch();
              }}
              disabled={isSyncing}
            >
              Filter
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
              disabled={isSyncing || (!startDate && !endDate)}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={
                isSyncing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshCw size={18} />
                )
              }
              onClick={handleSync}
              disabled={isSyncing}
            >
              {isSyncing ? "Syncing..." : "Force Sync"}
            </Button>
          </Stack>

          {/* Feedback Messages */}
          {syncStatus.message && (
            <Alert
              severity={syncStatus.type || "info"}
              sx={{ mt: 2 }}
              icon={
                syncStatus.type === "success" ? (
                  <CheckCircle2 size={20} />
                ) : syncStatus.type === "error" ? (
                  <AlertCircle size={20} />
                ) : undefined
              }
              onClose={() => setSyncStatus({ type: "", message: "" })}
            >
              {syncStatus.message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Results Table */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead
            sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}
          >
            <TableRow>
              <TableCell fontWeight="bold">Customer ID</TableCell>
              <TableCell align="right" fontWeight="bold">
                Total Calls
              </TableCell>
              <TableCell align="right" fontWeight="bold">
                Total Duration (Formatted)
              </TableCell>
              <TableCell align="right" fontWeight="bold">
                Total Seconds
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading usage data...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Alert severity="error" sx={{ display: "inline-flex" }}>
                    {error?.message || "Failed to fetch usage report."}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : !data?.data || data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No records found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((row) => (
                <TableRow key={row.customerId} hover>
                  <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight="medium">
                      {row.customerId || "Unknown"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={row.totalCalls?.toLocaleString() || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color="primary.main"
                      fontWeight="bold"
                    >
                      {formatDuration(row.totalBillingDuration)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {row.totalBillingDuration?.toLocaleString() || 0}s
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default VoiceUsageSync;
